import React, { ChangeEvent, useCallback, useState } from 'react';
import { Switch, useDisclosure, useToast } from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { AlertDialogModal } from 'clipsal-cortex-ui/src/components/AlertDialogModal';

import { getShutdownAlertUserPreference } from '../../account/settings/settings-helpers';
import { selectSite } from '../../site/siteSlice';
import { useUpdateSwitchMutation } from '../switches/switchApi';
import { getAylaDeviceType, useAylaSwitchData } from './ayla-switch-helpers';
import {
  useGetLocalAylaMatterDevice,
  useTurnLocalAylaMatterDeviceOnOffMutation,
} from './matter/localAylaMatterDevicesApi';

type Props = {
  switchId: number;
};

export function AylaSwitchToggle({ switchId }: Props) {
  const { data: aylaSwitch, isLoading, isLiveDataLoading } = useAylaSwitchData(switchId);
  const { display_name: displayName, state, site_switch_label: siteSwitchLabel } = aylaSwitch;
  const { isMatterDevice } = getAylaDeviceType(displayName);
  const { data: localAylaDevice, isLoading: isLocalDataLoading } = useGetLocalAylaMatterDevice(
    aylaSwitch?.oem_switch_id,
    isLoading || !isMatterDevice
  );
  const [updateSwitch, { isLoading: updateIsLoading }] = useUpdateSwitchMutation();
  const [turnLocalMatterDeviceOnOff, { isLoading: localUpdateIsLoading }] = useTurnLocalAylaMatterDeviceOnOffMutation();
  const toast = useToast({ isClosable: true });
  const { isOpen: isShutdownAlertOpen, onOpen: onOpenShutdownAlert, onClose: onCloseShutdownAlert } = useDisclosure();
  const [updateUserPreferenceRequested, setUpdateUserPreferenceRequested] = useState<boolean>(false);
  const { site_id: siteId } = useSelector(selectSite);
  const { t } = useTranslation();
  const showShutdownAlert = getShutdownAlertUserPreference();
  const deviceIsOn = state === 'closed';
  const isLoaded = !isLoading && !isLiveDataLoading && !isLocalDataLoading && !updateIsLoading && !localUpdateIsLoading;

  const handleSwitchToggle = useCallback(
    async (toggleOnRequested: boolean) => {
      const requestedState = toggleOnRequested ? 'closed' : 'open';

      try {
        if (isMatterDevice && localAylaDevice?.matterDeviceId) {
          // If the Ayla device uses Matter, we attempt to switch it locally. Only works if available locally.
          turnLocalMatterDeviceOnOff({
            matterDeviceId: localAylaDevice.matterDeviceId,
            state: requestedState,
          });
        }

        await updateSwitch({ siteId, switchId, body: { state: requestedState } }).unwrap();
      } catch (error) {
        console.error(error);
        toast({
          status: 'error',
          title: t('Devices.error updating device'),
          description: `${t('Common.please try again')} ${t('Common.if this persists contact support')}`,
        });
      }
    },
    [
      isMatterDevice,
      localAylaDevice?.matterDeviceId,
      siteId,
      switchId,
      t,
      toast,
      turnLocalMatterDeviceOnOff,
      updateSwitch,
    ]
  );

  return (
    <>
      <Switch
        isChecked={deviceIsOn}
        onChange={(e: ChangeEvent<HTMLInputElement>) => {
          if (!e.target.checked && showShutdownAlert) {
            onOpenShutdownAlert();
          } else handleSwitchToggle(e.target.checked);
        }}
        variant="solarGreen"
        size="lg"
        data-testid={`ayla-switch-${switchId}-toggle`}
        isDisabled={!isLoaded}
      />
      <AlertDialogModal
        isOpen={isShutdownAlertOpen}
        onClose={onCloseShutdownAlert}
        header={t('Devices.turn off device?', { name: siteSwitchLabel })}
        subHeader={t('Devices.are you sure turn off?')}
        confirmButtonName={t('Devices.turn off')}
        cancelButtonName={t('Common.cancel')}
        onConfirm={() => {
          updateUserPreferenceRequested && localStorage.setItem('showShutdownAlerts', 'false');
          handleSwitchToggle(false);
        }}
        onSelectDontShowAgain={() => setUpdateUserPreferenceRequested(true)}
        dontShowRadioText={t('Common.dont show alerts anymore')}
      />
    </>
  );
}
